import { isSameYear, parseISO } from 'date-fns';
import { isBoolean, isArray, isObject, memoize } from 'lodash';

import { common, model } from '@gosupersimple/types';
import { toZonedTime } from 'date-fns-tz';

import {
  formatDate,
  getIntervalFormat,
  getWeekdayName,
  isInterval,
  justifyInterval,
} from '@/lib/date';

import { Field, TimeAggregationPeriod } from '../types';
import { isNumberType } from '.';

const ONE_MILLION = 1e6;

const isLessThanMillion = (
  value: number,
  format:
    | common.NumberPropertyValueFormat
    | common.FloatPropertyValueFormat
    | common.IntegerPropertyValueFormat
    | undefined,
) => Math.abs(value * (format === 'percentage' ? 100 : 1)) < ONE_MILLION;

export const limitLength = (value: string, maxLength: number | undefined) =>
  maxLength !== undefined && value.length > maxLength
    ? `${value.substring(0, maxLength - 3)}...`
    : value;

type NumberStyle = 'compact' | 'compactLong' | 'compactLongUnderMillion';

export interface PropertyFormatOptions {
  field?: Field;
  type?: model.PropertyType | null;
  locale?: string;
  maxLength?: number;
  timezone?: string;
  precision?: TimeAggregationPeriod;
  format?: common.PropertyValueFormat;
  style?: NumberStyle;
  /**
   * Override the maximum number of fraction digits for compact-style number
   * formatting. Used by chart axes to show more decimals for small value ranges
   * (e.g. 0.01) while keeping the default single-decimal compact display.
   */
  maxFractionDigits?: number;
}

const getNumberFormat = memoize(
  (
    locale?: string,
    format?:
      | common.NumberPropertyValueFormat
      | common.FloatPropertyValueFormat
      | common.IntegerPropertyValueFormat,
    style?: NumberStyle,
    maxFractionDigits?: number,
  ) => {
    const compactOverrides =
      style === 'compact'
        ? {
            notation: 'compact' as const,
            minimumFractionDigits: 0,
            maximumFractionDigits: maxFractionDigits ?? 1,
          }
        : {};
    const compactLongOverrides =
      style === 'compactLong'
        ? {
            notation: 'compact' as const,
            compactDisplay: 'long' as const,
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
          }
        : {};
    const compactLongUnderMillionOverrides =
      style === 'compactLongUnderMillion'
        ? {
            notation: undefined,
            compactDisplay: undefined,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }
        : {};

    switch (format) {
      case 'percentage':
        return new Intl.NumberFormat(locale, {
          style: 'percent',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          ...compactOverrides,
          ...compactLongOverrides,
          ...compactLongUnderMillionOverrides,
        });
      case 'eur':
      case 'usd':
      case 'gbp':
      case 'mxn':
      case 'cad':
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: format.toUpperCase(),
          maximumFractionDigits: 2,
          currencyDisplay: 'narrowSymbol',
          ...compactOverrides,
          ...compactLongOverrides,
          ...compactLongUnderMillionOverrides,
        });
      default:
        return new Intl.NumberFormat(undefined, {
          maximumFractionDigits: 6,
          ...compactOverrides,
          ...compactLongOverrides,
          ...compactLongUnderMillionOverrides,
        });
    }
  },
  (locale, format, style, maxFractionDigits) =>
    JSON.stringify({ locale, format, style, maxFractionDigits }),
);

/**
 * How many fraction digits compact formatting needs to keep a value readable.
 * Values >= 0.1 keep the default single decimal; smaller values widen precision
 * by one digit per leading zero so e.g. 0.050675 shows as "0.05" instead of
 * "0.1" and 0.001 doesn't collapse to "0". Trailing zeros are dropped by the
 * formatter, so this is safe to apply broadly.
 */
export const getCompactFractionDigits = (value: number): number => {
  const magnitude = Math.abs(value);
  if (!isFinite(magnitude) || magnitude === 0) {
    return 1;
  }
  // The epsilon guards against float error in log10 (e.g. log10(0.01) coming
  // back as -2.0000000001 and over-counting a digit).
  return Math.max(1, Math.ceil(-Math.log10(magnitude) - 1e-9));
};

const getNumberFormatFn = (
  locale?: string,
  format?:
    | common.NumberPropertyValueFormat
    | common.FloatPropertyValueFormat
    | common.IntegerPropertyValueFormat,
  style?: NumberStyle,
  maxFractionDigits?: number,
) => {
  return (value: number) => {
    if (isLessThanMillion(value, format) && style === 'compactLong') {
      style = 'compactLongUnderMillion';
    }
    if (Object.is(value, -0)) {
      value = 0;
    }
    const effectiveMaxFractionDigits =
      style === 'compact' && maxFractionDigits === undefined
        ? getCompactFractionDigits(format === 'percentage' ? value * 100 : value)
        : maxFractionDigits;
    const numberFormat = getNumberFormat(locale, format, style, effectiveMaxFractionDigits);
    return numberFormat.format(value);
  };
};

export const formatNumber = (
  value: number,
  format?: common.PropertyValueFormat,
  style?: NumberStyle,
  locale?: string,
  maxFractionDigits?: number,
) => {
  switch (format) {
    case 'eur':
    case 'usd':
    case 'gbp':
    case 'mxn':
    case 'cad':
    case 'percentage':
      return getNumberFormatFn(locale, format, style, maxFractionDigits)(value);
    default:
      return getNumberFormatFn(locale, undefined, style, maxFractionDigits)(value);
  }
};

const getYearFormat = memoize(
  (locale?: string) => new Intl.DateTimeFormat(locale, { year: 'numeric' }),
);
const getMonthFormat = memoize(
  (locale?: string) => new Intl.DateTimeFormat(locale, { month: 'numeric' }),
);
const getDayFormat = memoize(
  (locale?: string) => new Intl.DateTimeFormat(locale, { day: 'numeric' }),
);
const getHourFormat = memoize(
  (locale?: string) => new Intl.DateTimeFormat(locale, { hour: 'numeric' }),
);

const formatDatePart = (value: number, part: common.TimeInterval, locale?: string) => {
  switch (part) {
    case 'year':
      return getYearFormat(locale).format(new Date(value, 0));
    case 'quarter':
      return String(value);
    case 'month':
      return getMonthFormat(locale).format(new Date(2025, value - 1));
    case 'week':
      return String(value);
    case 'day':
      return value < 32 ? getDayFormat(locale).format(new Date(2025, 0, value)) : String(value);
    case 'day_of_week':
      return getWeekdayName(value);
    case 'hour':
      return getHourFormat(locale).format(new Date(2025, 0, 1, value));
    default:
      return '';
  }
};

export function formatPropertyValue(
  value: any,
  {
    field,
    type = null,
    locale,
    maxLength,
    timezone,
    precision,
    format,
    style,
    maxFractionDigits,
  }: PropertyFormatOptions = {},
): string {
  type = type ?? field?.type ?? null;
  precision = precision ?? field?.precision;
  format =
    format ??
    field?.format ??
    ((field?.pk === true || field?.relation !== undefined) && field.type !== 'Date'
      ? 'raw'
      : undefined);

  if (value === null || value === '' || value === undefined) {
    // TODO: better handle nully Model properties
    return '-';
  }

  if (type === 'Integer' && format === 'date') {
    return precision !== undefined ? formatDatePart(value, precision, locale) : String(value);
  }

  if (type === 'Date' && typeof value === 'string' && format !== 'raw') {
    try {
      const d = parseISO(value);
      if (format === 'iso') {
        return value;
      }
      const shouldShowYear = !isSameYear(
        toZonedTime(new Date(), timezone ?? 'UTC'),
        toZonedTime(d, timezone ?? 'UTC'),
      );
      let dateFormat = `MMMM do${shouldShowYear ? ' yyyy' : ''} HH:mm`;
      if (format === 'date' && precision === undefined) {
        dateFormat = `MMMM do${shouldShowYear ? ' yyyy' : ''}`;
      }
      if (format === 'date' || precision !== undefined) {
        dateFormat = getIntervalFormat(precision ?? 'day');
      }
      if (format === 'time') {
        dateFormat = 'HH:mm';
      }
      return formatDate(d, dateFormat, timezone);
    } catch {
      return limitLength(String(value), maxLength);
    }
  }
  if ((type === 'Boolean' || isBoolean(value)) && format !== 'raw') {
    if (value === true || value === 'true') {
      return 'Yes';
    }
    if (value === false || value === 'false') {
      return 'No';
    }
  }
  if (isNumberType(type) && !isNaN(Number(value)) && format !== 'raw') {
    return formatNumber(Number(value), format, style, locale, maxFractionDigits);
  }
  if (type === 'Object') {
    if (isObject(value)) {
      return limitLength(JSON.stringify(value, null, 2), maxLength);
    }
    return value;
  }
  if (typeof value === 'string') {
    return limitLength(value, maxLength);
  }
  if (type === 'Array' || (type === null && Array.isArray(value))) {
    if (isArray(value) && value.some((item) => isObject(item))) {
      return limitLength(JSON.stringify(value, null, 2), maxLength);
    }
    if (isArray(value)) {
      return limitLength(value.join(', '), maxLength);
    }
    return limitLength(JSON.stringify(value), maxLength);
  }
  if ((type === 'Interval' || type === 'String') && isInterval(value)) {
    const interval = justifyInterval(value);
    const result = [];
    if (interval.years !== undefined) {
      result.push(`${interval.years} year${Math.abs(interval.years) !== 1 ? 's' : ''}`);
    }
    if (interval.months !== undefined) {
      result.push(`${interval.months} month${Math.abs(interval.months) !== 1 ? 's' : ''}`);
    }
    if (interval.days !== undefined) {
      result.push(`${interval.days} day${Math.abs(interval.days) !== 1 ? 's' : ''}`);
    }
    if (interval.hours !== undefined) {
      result.push(`${interval.hours} hour${Math.abs(interval.hours) !== 1 ? 's' : ''}`);
    }
    if (interval.minutes !== undefined) {
      result.push(`${interval.minutes} minute${Math.abs(interval.minutes) !== 1 ? 's' : ''}`);
    }
    if (interval.seconds !== undefined) {
      result.push(`${interval.seconds} second${Math.abs(interval.seconds) !== 1 ? 's' : ''}`);
    }
    if (interval.milliseconds !== undefined) {
      result.push(
        `${interval.milliseconds} millisecond${Math.abs(interval.milliseconds) !== 1 ? 's' : ''}`,
      );
    }
    return result.length === 0 ? '0' : result.join(', ');
  }
  return limitLength(String(value), maxLength);
}
